import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/auth";
import { Skel } from "@/components/market/Shared";
import { cents, endsIn } from "./utils";

export const OddsPair = ({ m, onOpen }) => {
  const yes = m.yes_name === "Yes" || m.yes_name.length > 24 ? m.short : m.yes_name;
  return (
    <div className="grid grid-cols-2 gap-2 min-w-0">
      <button onClick={() => onOpen(m.slug)} className="odds-yes" data-testid={`odds-yes-${m.slug}`}><span className="odds-label">{yes}</span><span className="odds-price">{cents(m.mid)}</span></button>
      <button onClick={() => onOpen(m.slug)} className="odds-no" data-testid={`odds-no-${m.slug}`}><span className="odds-label">{m.no_name}</span><span className="odds-price">{m.mid == null ? "—" : cents(1 - m.mid)}</span></button>
    </div>
  );
};

const Side = ({ m, onOpen }) => (
  <div className="min-w-0 space-y-1" data-testid={`game-mkt-${m.slug}`}>
    <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500 font-bold"><span>{m.kind}</span>
      {m.pick && <span className="text-[var(--violet)] normal-case tracking-normal">Data pick: {m.pick.name} {Math.round(m.pick.chance * 100)}%</span>}</div>
    <OddsPair m={m} onOpen={onOpen} />
  </div>
);

const Game = ({ g, onOpen }) => (
  <div className="glass p-4 space-y-2 min-w-0 overflow-hidden" data-testid={`game-${g.key}`}>
    <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
      <span className="uppercase tracking-wider font-semibold">{g.league_name}</span>
      {g.live ? <span className="flex items-center gap-1.5 text-rose-400 font-bold"><span className="live-dot" />LIVE</span> :
        <span className="font-num">{new Date(g.start).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })} · in {endsIn({ group: "sports", game_start: g.start })}</span>}
    </div>
    <div className="font-head font-bold text-xl leading-tight">{g.title}</div>
    <div className="space-y-2.5">{g.markets.slice(0, 3).map((m) => <Side key={m.slug} m={m} onOpen={onOpen} />)}</div>
    {g.markets.length > 3 && <div className="text-[11px] text-slate-500">+{g.markets.length - 3} more lines</div>}
  </div>
);

export const SportsBoard = ({ q, onOpen }) => {
  const [league, setLeague] = useState("all");
  const [tab, setTab] = useState("games");
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    const t = setTimeout(() => axios.get(`${API}/predict/sports`, { params: { league, q } }).then((r) => setD(r.data)).catch(() => setD({ error: true })), q ? 350 : 0);
    const iv = setInterval(() => axios.get(`${API}/predict/sports`, { params: { league, q } }).then((r) => setD(r.data)).catch(() => {}), 45000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [league, q]);
  return (
    <section className="space-y-3 min-w-0" data-testid="sports-board">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[{ id: "all", name: "All leagues" }, ...(d?.leagues || []).slice(0, 16)].map((l) => (
          <button key={l.id} onClick={() => setLeague(l.id)} data-testid={`league-${l.id}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${league === l.id ? "border-[var(--cyan)] text-[var(--cyan)]" : "border-[var(--line)] text-slate-400"}`}>{l.name}{l.count ? <span className="opacity-60 ml-1 font-num">{l.count}</span> : null}</button>))}
      </div>
      <div className="inline-flex rounded-full border border-[var(--line)] p-1">
        {[["games", `Games${d?.games_total != null ? ` (${d.games_total})` : ""}`], ["futures", `Futures${d?.futures_total != null ? ` (${d.futures_total})` : ""}`]].map(([id, n]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`sports-tab-${id}`} className={`px-4 py-1.5 rounded-full text-xs font-semibold ${tab === id ? "bg-[var(--cyan)] text-[#03141A]" : "text-slate-400"}`}>{n}</button>))}
      </div>
      {!d ? <Skel h="h-48" /> : d.error ? <p className="text-sm text-slate-400">Sports markets are unavailable right now.</p> :
        tab === "games" ? (d.games.length ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 min-w-0">{d.games.map((g) => <Game key={g.key} g={g} onOpen={onOpen} />)}</div> : <p className="glass p-5 text-sm text-slate-400" data-testid="sports-no-games">No upcoming games in this league right now. Check Futures.</p>) :
          <div className="glass divide-y divide-[var(--line)]">{d.futures.map((m) => (
            <button key={m.slug} onClick={() => onOpen(m.slug)} className="w-full flex justify-between gap-3 px-4 py-2.5 text-sm text-left hover:bg-white/[0.03]" data-testid={`future-${m.slug}`}>
              <span className="min-w-0 truncate">{m.label}</span><span className="font-num text-emerald-300 shrink-0">{cents(m.mid)}</span></button>))}</div>}
    </section>
  );
};
